import type {
  AdminBundle,
  AdminChecklist,
  AdminGradeField,
  AdminLesson,
  AdminPathStep,
  AdminProfile,
  AdminSampleShot,
  AdminTrack,
  ContentBundle,
  PhotoRule,
  Progress,
  StatsOverview,
  VendorProfile,
} from './types';

/**
 * Same-origin API client.
 *
 * There is no base URL and no token handling: one Express process serves both this SPA
 * and /api, and the session rides in an httpOnly cookie the browser attaches on its own.
 * That cookie is not readable from JS by design.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let code: string | undefined;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
      code = body?.code;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new ApiError(message, res.status, code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });

const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

/** Fields the API accepts on write — `_id` and `order` are server-owned. */
type TrackInput = Omit<AdminTrack, '_id' | 'order'> & { order?: number };
type LessonInput = Omit<AdminLesson, '_id' | 'order'> & { order?: number };
type ChecklistInput = Omit<AdminChecklist, '_id' | 'order'> & { order?: number };
type SampleShotInput = Pick<AdminSampleShot, 'slug' | 'label' | 'hint'> & { order?: number };

export const api = {
  /**
   * Cold-load session probe. Always 200; `vendor: null` means nobody is signed in, so
   * this never logs a spurious 401 in the console for a first-time visitor.
   */
  session: () =>
    request<{ vendor: VendorProfile | null; admin: AdminProfile | null }>('/auth/session'),

  /** One sign-in for both roles; the server resolves which and the caller redirects. */
  login: (email: string, password: string) =>
    post<{ role: 'vendor' | 'admin'; vendor: VendorProfile | null; admin: AdminProfile | null }>(
      '/auth/login',
      { email, password },
    ),

  vendorLogin: (email: string, password: string) =>
    post<{ vendor: VendorProfile }>('/auth/vendor/login', { email, password }),
  vendorMe: () => request<{ vendor: VendorProfile }>('/auth/vendor/me'),

  adminLogin: (email: string, password: string) =>
    post<{ admin: AdminProfile }>('/auth/admin/login', { email, password }),
  adminMe: () => request<{ admin: AdminProfile }>('/auth/admin/me'),

  logout: () => post<{ ok: true }>('/auth/logout'),

  content: () => request<ContentBundle>('/content'),

  progress: () => request<Progress>('/progress'),
  setProgress: (bucket: keyof Progress, key: string, value: boolean) =>
    request<Progress>('/progress', {
      method: 'PATCH',
      body: JSON.stringify({ bucket, key, value }),
    }),
  clearProgress: (bucket: keyof Progress) => post<Progress>('/progress/clear', { bucket }),

  /**
   * The editor's content. Raw documents with `_id` and `published`, unlike
   * `content()` above, which is the vendor renderer's view.
   */
  adminContent: () => request<AdminBundle>('/admin/content'),

  /** Enrolment and progress numbers for the overview screen. */
  stats: () => request<StatsOverview>('/admin/stats'),

  createTrack: (body: TrackInput) => post<AdminTrack>('/admin/tracks', body),
  updateTrack: (id: string, body: Partial<TrackInput>) => put<AdminTrack>(`/admin/tracks/${id}`, body),
  deleteTrack: (id: string) => del<{ ok: true }>(`/admin/tracks/${id}`),
  reorderTracks: (ids: string[]) => post<{ ok: true }>('/admin/tracks/reorder', { ids }),

  createLesson: (body: LessonInput) => post<AdminLesson>('/admin/lessons', body),
  updateLesson: (id: string, body: Partial<LessonInput>) =>
    put<AdminLesson>(`/admin/lessons/${id}`, body),
  deleteLesson: (id: string) => del<{ ok: true }>(`/admin/lessons/${id}`),
  reorderLessons: (ids: string[]) => post<{ ok: true }>('/admin/lessons/reorder', { ids }),

  createChecklist: (body: ChecklistInput) => post<AdminChecklist>('/admin/checklists', body),
  updateChecklist: (id: string, body: Partial<ChecklistInput>) =>
    put<AdminChecklist>(`/admin/checklists/${id}`, body),
  deleteChecklist: (id: string) => del<{ ok: true }>(`/admin/checklists/${id}`),

  createSampleShot: (body: SampleShotInput) => post<AdminSampleShot>('/admin/sample-shots', body),
  updateSampleShot: (id: string, body: Partial<SampleShotInput>) =>
    put<AdminSampleShot>(`/admin/sample-shots/${id}`, body),
  deleteSampleShot: (id: string) => del<{ ok: true }>(`/admin/sample-shots/${id}`),
  /** `dataUrl` is a downscaled image — see SampleShotsAdmin for why the client resizes. */
  uploadSampleShotImage: (id: string, dataUrl: string) =>
    put<{ ok: true; bytes: number }>(`/admin/sample-shots/${id}/image`, { dataUrl }),
  removeSampleShotImage: (id: string) => del<{ ok: true }>(`/admin/sample-shots/${id}/image`),

  // These three are replace-the-whole-list endpoints, not per-row CRUD.
  savePath: (steps: AdminPathStep[]) => put<{ ok: true; count: number }>('/admin/path', { steps }),
  savePhotoRules: (rules: PhotoRule[]) =>
    put<{ ok: true; count: number }>('/admin/photo-rules', { rules }),
  saveGradeFields: (fields: AdminGradeField[]) =>
    put<{ ok: true; count: number }>('/admin/grade-fields', { fields }),
};
